/**
 * Building a thing out of a handful of parts.
 *
 * Not a modelling program, and not trying to be. A box, a cylinder, a cone and a
 * ball, each with a size, a colour and a place to sit, which is enough for a
 * crate to read as a crate and a signpost as a signpost. Anything past that
 * wants a modeller and a file, which is a different job with a different set of
 * problems: uploads, formats, licences, and somebody's laptop.
 *
 * Written straight into the scenery it belongs to, so what is drawn in the world
 * and what is drawn in the browser are the same thing.
 */

import { useState } from 'react';
import type { AdminCommand } from '../Admin';
import { Explain } from '../Explain';

export interface ShapePiece {
  shape: 'box' | 'cylinder' | 'cone' | 'sphere';
  colour: string;
  width: number;
  height: number;
  depth: number;
  offset: number;
  /** Where it sits across the tile from the middle: sideways, and forwards. */
  across: number;
  along: number;
  turn: number;
  /** How far off vertical it stands, in degrees, and which way it goes. */
  lean?: number;
  leanTo?: number;
  glow?: number;
  glowsAt?: string;
  opacity?: number;
  casts?: number;
  flickers?: boolean;
  shifts?: boolean;
  sways?: number;
  turns?: number;
  turnsOn?: string;
  bobs?: number;
}

const SHAPES: ShapePiece['shape'][] = ['box', 'cylinder', 'cone', 'sphere'];

/** A few colours that look like the world rather than like a paint program. */
const COLOURS = ['8a7f6a', '6f5940', '4a6b3a', '6a6560', 'a9926a', '7a5a38', 'c8a86a', '2b4c63'];

export function ShapeBuilder({
  objectId,
  parts,
  onCommand,
}: {
  objectId: number;
  parts: readonly ShapePiece[];
  onCommand: AdminCommand;
}): JSX.Element {
  const [open, setOpen] = useState(parts.length > 0);

  const send = (next: ShapePiece[]): void => {
    onCommand('setObject', { object: objectId }, { parts: JSON.stringify(next) });
  };

  const change = (at: number, patch: Partial<ShapePiece>): void => {
    send(parts.map((part, index) => (index === at ? { ...part, ...patch } : part)));
  };

  if (!open) {
    return (
      <button type="button" className="wide" onClick={() => setOpen(true)}>
        Build a shape for it
      </button>
    );
  }

  return (
    <div className="editor-section">
      <h4>Built out of</h4>

      {parts.map((part, at) => (
        <div className="editor-piece-card" key={at}>
          <div className="editor-condition">
            <select
              value={part.shape}
              onChange={(event) => change(at, { shape: event.target.value as ShapePiece['shape'] })}
            >
              {SHAPES.map((shape) => (
                <option key={shape} value={shape}>
                  {shape}
                </option>
              ))}
            </select>

            <select
              value={part.colour}
              onChange={(event) => change(at, { colour: event.target.value })}
            >
              {COLOURS.map((colour) => (
                <option key={colour} value={colour}>
                  {colour}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="editor-tab"
              title="Take this part off"
              onClick={() => send(parts.filter((_, index) => index !== at))}
            >
              ✕
            </button>
          </div>

          <div className="editor-condition">
            <label className="editor-number">
              <span>across</span>
              <input
                type="number"
                step={0.05}
                value={part.width}
                onChange={(event) => change(at, { width: Number(event.target.value) })}
              />
            </label>
            <label className="editor-number">
              <span>tall</span>
              <input
                type="number"
                step={0.05}
                value={part.height}
                onChange={(event) => change(at, { height: Number(event.target.value) })}
              />
            </label>
            <label className="editor-number">
              <span>deep</span>
              <input
                type="number"
                step={0.05}
                value={part.depth}
                onChange={(event) => change(at, { depth: Number(event.target.value) })}
              />
            </label>
            <label className="editor-number">
              <span>up</span>
              <input
                type="number"
                step={0.05}
                value={part.offset}
                onChange={(event) => change(at, { offset: Number(event.target.value) })}
              />
            </label>
            <label className="editor-number">
              <span>side</span>
              <input
                type="number"
                step={0.05}
                value={part.across ?? 0}
                onChange={(event) => change(at, { across: Number(event.target.value) })}
              />
            </label>
            <label className="editor-number">
              <span>fwd</span>
              <input
                type="number"
                step={0.05}
                value={part.along ?? 0}
                onChange={(event) => change(at, { along: Number(event.target.value) })}
              />
            </label>
            <button
              type="button"
              className="editor-tab"
              title="Turn this part a quarter"
              onClick={() => change(at, { turn: (part.turn + 1) % 4 })}
            >
              turn
            </button>
            {/*
              How far off vertical it stands and which way it goes. It pivots
              at the foot, so a reed leans out of the mud it grows in rather
              than hovering beside it.
            */}
            <label className="editor-number">
              <span>lean</span>
              <input
                type="number"
                step={5}
                min={-85}
                max={85}
                value={part.lean ?? 0}
                onChange={(event) => change(at, { lean: Number(event.target.value) })}
              />
            </label>
            <label className="editor-number">
              <span>way</span>
              <input
                type="number"
                step={15}
                min={0}
                max={359}
                value={part.leanTo ?? 0}
                onChange={(event) => change(at, { leanTo: Number(event.target.value) })}
              />
            </label>
            {/*
              How much light it carries of itself, for anything that should not
              go dark on the side the sun is not on: falling water, a flame, a
              lamp, lava.
            */}
            <label className="editor-number">
              <span>glow</span>
              <input
                type="number"
                step={0.1}
                min={0}
                max={1}
                value={part.glow ?? 0}
                onChange={(event) => change(at, { glow: Number(event.target.value) })}
              />
            </label>
            {/* When that light is on. A waterfall is bright at any hour; a
                window is only lit after dark. */}
            <button
              type="button"
              className={part.glowsAt === 'night' ? 'editor-tab on' : 'editor-tab'}
              title="Only after dark, rather than at every hour"
              hidden={(part.glow ?? 0) === 0 && (part.casts ?? 0) === 0}
              onClick={() =>
                change(at, { glowsAt: part.glowsAt === 'night' ? 'always' : 'night' })
              }
            >
              at night
            </button>
            {/* How much of it there is to see through. One is solid. */}
            <label className="editor-number">
              <span>clear</span>
              <input
                type="number"
                step={0.1}
                min={0.05}
                max={1}
                value={part.opacity ?? 1}
                onChange={(event) =>
                  change(at, { opacity: Math.min(1, Math.max(0.05, Number(event.target.value))) })
                }
              />
            </label>
            {/* A real light on what is around it, as opposed to the part merely
                looking bright. Only the nearest few in the world are lit at
                once, so this is a want rather than a promise. */}
            <label className="editor-number">
              <span>casts</span>
              <input
                type="number"
                step={1}
                min={0}
                max={14}
                value={part.casts ?? 0}
                onChange={(event) =>
                  change(at, { casts: Math.min(14, Math.max(0, Number(event.target.value))) })
                }
              />
            </label>
            <button
              type="button"
              className={part.flickers ? 'editor-tab on' : 'editor-tab'}
              title="Wavers the way a flame does, shape and light together"
              onClick={() => change(at, { flickers: part.flickers !== true })}
            >
              flickers
            </button>
            <button
              type="button"
              className={part.shifts ? 'editor-tab on' : 'editor-tab'}
              title="Walks its colour round the wheel, for light that is not burning"
              onClick={() => change(at, { shifts: part.shifts !== true })}
            >
              shifts
            </button>
            {/* What the wind does to it, how fast it turns, and whether it
                floats. All three pivot or measure from the foot, the same way
                the lean does. */}
            <label className="editor-number">
              <span>sways</span>
              <input
                type="number"
                step={0.05}
                min={0}
                max={1}
                value={part.sways ?? 0}
                onChange={(event) =>
                  change(at, { sways: Math.min(1, Math.max(0, Number(event.target.value))) })
                }
              />
            </label>
            <label className="editor-number">
              <span>turns</span>
              <input
                type="number"
                step={1}
                min={-60}
                max={60}
                value={part.turns ?? 0}
                onChange={(event) =>
                  change(at, { turns: Math.min(60, Math.max(-60, Number(event.target.value))) })
                }
              />
            </label>
            {/* Which way up that turning is: a sign sweeps round, a sail goes
                end over end. */}
            <select
              value={part.turnsOn ?? 'upright'}
              hidden={(part.turns ?? 0) === 0}
              title="Which way the turning lies"
              onChange={(event) => change(at, { turnsOn: event.target.value })}
            >
              <option value="upright">upright</option>
              <option value="across">across</option>
              <option value="along">along</option>
            </select>
            <label className="editor-number">
              <span>bobs</span>
              <input
                type="number"
                step={0.05}
                min={0}
                max={2}
                value={part.bobs ?? 0}
                onChange={(event) =>
                  change(at, { bobs: Math.min(2, Math.max(0, Number(event.target.value))) })
                }
              />
            </label>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="wide"
        onClick={() =>
          send([
            ...parts,
            {
              shape: 'box',
              colour: COLOURS[parts.length % COLOURS.length] ?? '8a7f6a',
              width: 0.6,
              height: 0.6,
              depth: 0.6,
              offset: 0.3 + parts.length * 0.4,
              across: 0,
              along: 0,
              turn: 0,
              lean: 0,
              leanTo: 0,
              glow: 0,
              glowsAt: 'always',
              opacity: 1,
              casts: 0,
              flickers: false,
              shifts: false,
              sways: 0,
              turns: 0,
              turnsOn: 'upright',
              bobs: 0,
            },
          ])
        }
      >
        Add a part
      </button>

      {parts.length > 0 ? (
        <button type="button" className="wide danger" onClick={() => send([])}>
          Back to its usual shape
        </button>
      ) : null}

      <p className="hint">
        Save the scenery to keep it.
        <Explain>
          Drawn by the same code the world draws everything else with, so what is here is what is
          out there.
        </Explain>
      </p>
    </div>
  );
}
